
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import ConfirmationModal from '../Shared/ConfirmationModal';

export interface ModalOptions {
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    isDestructive?: boolean;
    showCancel?: boolean;
    confirmStyle?: 'primary' | 'danger' | 'success' | 'pending' | 'secondary';
    hideIcon?: boolean;
}

export interface ThreeWayModalOptions extends ModalOptions {
    thirdActionLabel: string;
    thirdActionStyle?: 'primary' | 'secondary' | 'danger';
}

export type ModalResult = 'confirm' | 'cancel' | 'third_action';

interface ModalContextType {
    showConfirm: (options: ModalOptions) => Promise<boolean>;
    showThreeWayConfirm: (options: ThreeWayModalOptions) => Promise<ModalResult>;
    showAlert: (options: string | Omit<ModalOptions, 'cancelLabel'>) => Promise<void>;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error('useModal must be used within a ModalProvider');
    }
    return context;
};

// Singleton-ish service for non-react calls (if absolutely necessary)
// but usually we prefer hooks.
export const modalService = {
    showConfirm: async (options: ModalOptions): Promise<boolean> => false,
    showThreeWayConfirm: async (options: ThreeWayModalOptions): Promise<ModalResult> => 'cancel',
    showAlert: async (options: string | Omit<ModalOptions, 'cancelLabel'>): Promise<void> => {}
};

export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [modalState, setModalState] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        confirmLabel?: string;
        cancelLabel?: string;
        thirdActionLabel?: string;
        thirdActionStyle?: 'primary' | 'secondary' | 'danger';
        isDestructive?: boolean;
        showCancel: boolean;
        confirmStyle?: 'primary' | 'danger' | 'success' | 'pending' | 'secondary';
        hideIcon?: boolean;
        resolve: (value: any) => void;
    } | null>(null);

    const showConfirm = useCallback((options: ModalOptions): Promise<boolean> => {
        return new Promise((resolve) => {
            setModalState({
                ...options,
                isOpen: true,
                showCancel: options.showCancel !== false,
                resolve
            });
        });
    }, []);

    const showThreeWayConfirm = useCallback((options: ThreeWayModalOptions): Promise<ModalResult> => {
        return new Promise((resolve) => {
            setModalState({
                ...options,
                isOpen: true,
                showCancel: options.showCancel !== false,
                resolve
            });
        });
    }, []);

    const showAlert = useCallback((options: string | Omit<ModalOptions, 'cancelLabel'>): Promise<void> => {
        return new Promise((resolve) => {
            const config = typeof options === 'string' 
                ? { title: 'Alert', message: options } 
                : options;
            
            setModalState({
                ...config,
                isOpen: true,
                showCancel: false,
                resolve: () => resolve()
            });
        });
    }, []);

    // Sync with service
    modalService.showConfirm = showConfirm;
    modalService.showThreeWayConfirm = showThreeWayConfirm;
    modalService.showAlert = showAlert;

    const handleConfirm = () => {
        if (modalState) {
            modalState.resolve(modalState.thirdActionLabel ? 'confirm' : true);
            setModalState(null);
        }
    };

    const handleThirdAction = () => {
        if (modalState) {
            modalState.resolve('third_action');
            setModalState(null);
        }
    };

    const handleCancel = () => {
        if (modalState) {
            modalState.resolve(modalState.thirdActionLabel ? 'cancel' : false);
            setModalState(null);
        }
    };

    return (
        <ModalContext.Provider value={{ showConfirm, showThreeWayConfirm, showAlert }}>
            {children}
            {modalState && (
                <ConfirmationModal
                    isOpen={modalState.isOpen}
                    title={modalState.title}
                    message={modalState.message}
                    confirmLabel={modalState.confirmLabel}
                    cancelLabel={modalState.cancelLabel}
                    thirdActionLabel={modalState.thirdActionLabel}
                    thirdActionStyle={modalState.thirdActionStyle}
                    isDestructive={modalState.isDestructive}
                    showCancel={modalState.showCancel}
                    confirmStyle={modalState.confirmStyle}
                    hideIcon={modalState.hideIcon}
                    onConfirm={handleConfirm}
                    onThirdAction={handleThirdAction}
                    onClose={handleCancel}
                />
            )}
        </ModalContext.Provider>
    );
};
